import { ICellEditorComp, ICellEditorParams } from "ag-grid-enterprise";
import { popupEnterMovesToNextRowStart } from "../../../application/toolkit/ag_grid_shared/excel_navigation";

// A live getter, never a snapshot, so a suggestion source that changes after the grid was
// built (e.g. newly staged files) still reaches a freshly opened editor.
export interface SuggestionCellEditorParams extends ICellEditorParams {
  getSuggestions: () => string[];
  // value -> display text. Rows show the label, filtering matches label or value, and accepting
  // a row still writes the value.
  labels?: Record<string, string>;
}

let instanceSeq = 0;

interface Match {
  name: string;
  label: string;
  // 0 = prefix match, 1 = mid-string. Ties keep source order (Array#sort is stable).
  rank: number;
}

/**
 * Free-text editor with a filtered suggestion list. Not `agSelectCellEditor`/
 * `agRichSelectCellEditor`: those reject a typed value outside their list, which would break
 * this grid's out-of-list values -- blank seed rows, pasted text, and (for enum columns) any
 * value the server's download can emit that isn't in its own dropdown list (e.g.
 * `BUTTON_COLOR_MAP_INVERSE.fetch(raw_color, raw_color)`).
 *
 * The suggestion source is the only thing callers vary: staged attachment filenames filtered
 * by extension, or a column's server-declared `values:` list.
 */
export class SuggestionCellEditor implements ICellEditorComp<unknown, string> {
  private params!: SuggestionCellEditorParams;
  private container!: HTMLDivElement;
  private input!: HTMLInputElement;
  private list!: HTMLUListElement;
  private suggestions: string[] = [];
  private labels: Record<string, string> = {};
  private matches: Match[] = [];
  private highlightedIndex = -1;
  private listOpen = false;
  // Nothing is highlighted until the user types or arrows, so Enter on an untouched cell
  // commits what is already there instead of silently accepting suggestion 1.
  private engaged = false;
  private readonly instanceId = ++instanceSeq;

  private inputHandler = (): void => {
    this.engaged = true;
    this.applyFilter();
  };
  private keydownHandler = (event: KeyboardEvent): void => this.handleKeyDown(event);
  private enterHandler = (event: KeyboardEvent): void => popupEnterMovesToNextRowStart(this.params, event);
  private outsideClickHandler = (event: MouseEvent): void => {
    if (!this.container.contains(event.target as Node)) this.closeList();
  };

  init(params: SuggestionCellEditorParams): void {
    this.params = params;
    this.suggestions = params.getSuggestions ? params.getSuggestions() : [];
    this.labels = params.labels ?? {};

    this.container = document.createElement("div");
    this.container.className = "suggestion-cell-editor";
    if (params.labels) this.container.classList.add("suggestion-cell-editor--labelled");
    // Under document.body (isPopup) no --ag-* var resolves, so the cell's box and type are
    // copied across rather than inherited.
    const cellRect = params.eGridCell.getBoundingClientRect();
    const cellStyle = window.getComputedStyle(params.eGridCell);
    this.container.style.setProperty("--suggestion-cell-editor-cell-width", `${cellRect.width}px`);
    this.container.style.fontFamily = cellStyle.fontFamily;
    this.container.style.fontSize = cellStyle.fontSize;
    this.container.style.setProperty("--suggestion-cell-editor-row-height", `${cellRect.height}px`);
    this.container.style.setProperty("--suggestion-cell-editor-padding", cellStyle.paddingLeft);
    this.container.addEventListener("keydown", this.enterHandler);

    this.input = document.createElement("input");
    this.input.type = "text";
    this.input.className = "suggestion-cell-editor__input";
    this.input.value = params.value ?? "";
    this.input.autocomplete = "off";
    this.input.placeholder = "Search…";

    this.list = document.createElement("ul");
    this.list.className = "suggestion-cell-editor__list";
    this.list.id = `suggestion-cell-editor-list-${this.instanceId}`;
    this.list.setAttribute("role", "listbox");

    // Input first: the popup anchors at the cell's top-left, so anything above it would
    // push the input off the cell being edited.
    this.container.appendChild(this.input);

    if (this.suggestions.length > 0) {
      this.input.setAttribute("role", "combobox");
      this.input.setAttribute("aria-expanded", "false");
      this.input.setAttribute("aria-controls", this.list.id);
      this.input.setAttribute("aria-autocomplete", "list");
      this.input.addEventListener("input", this.inputHandler);
      this.input.addEventListener("keydown", this.keydownHandler);
      document.addEventListener("mousedown", this.outsideClickHandler);
      this.container.appendChild(this.list);
    }
  }

  getGui(): HTMLElement {
    return this.container;
  }

  getValue(): string {
    return this.input.value;
  }

  afterGuiAttached(): void {
    this.input.focus();
    this.input.select();
    if (this.suggestions.length > 0) this.applyFilter();
  }

  isPopup(): boolean {
    // The autoHeight viewport clips overflow unconditionally, so an inline list would be
    // cut off under rows near the bottom.
    return true;
  }

  destroy(): void {
    this.container.removeEventListener("keydown", this.enterHandler);
    this.input.removeEventListener("input", this.inputHandler);
    this.input.removeEventListener("keydown", this.keydownHandler);
    document.removeEventListener("mousedown", this.outsideClickHandler);
  }

  // ── Filtering ──────────────────────────────────────────────────────────

  private applyFilter(): void {
    const query = this.input.value.trim().toLowerCase();
    this.matches = this.rankMatches(query);
    this.renderList();
    this.openList();
  }

  private rankMatches(query: string): Match[] {
    if (query === "") return this.suggestions.map((name) => ({ name, label: this.labelFor(name), rank: 0 }));

    return this.suggestions
      .map((name): Match | null => {
        const label = this.labelFor(name);
        const indexes = [label, name].map((text) => text.toLowerCase().indexOf(query)).filter((index) => index !== -1);
        if (indexes.length === 0) return null;
        return { name, label, rank: indexes.includes(0) ? 0 : 1 };
      })
      .filter((match): match is Match => match !== null)
      .sort((a, b) => a.rank - b.rank);
  }

  private labelFor(name: string): string {
    return this.labels[name] ?? name;
  }

  // ── Rendering ──────────────────────────────────────────────────────────

  private renderList(): void {
    this.list.innerHTML = "";
    this.highlightedIndex = this.engaged && this.matches.length > 0 ? 0 : -1;

    if (this.matches.length === 0) {
      const empty = document.createElement("li");
      empty.className = "suggestion-cell-editor__empty";
      empty.textContent = "No matches";
      this.list.appendChild(empty);
      this.updateActiveDescendant();
      return;
    }

    this.matches.forEach((match, index) => {
      const row = document.createElement("li");
      row.id = this.rowId(index);
      row.className = "suggestion-cell-editor__option";
      row.setAttribute("role", "option");
      const text = document.createElement("span");
      text.className = "suggestion-cell-editor__option-label";
      text.textContent = match.label;
      row.appendChild(text);
      row.addEventListener("mousedown", (event) => {
        // Blur before the click completes would tear down the list first.
        event.preventDefault();
        this.acceptRow(index);
      });
      this.list.appendChild(row);
    });

    this.updateHighlight();
  }

  private rowId(index: number): string {
    return `suggestion-cell-editor-option-${this.instanceId}-${index}`;
  }

  private updateHighlight(): void {
    const rows = Array.from(this.list.children) as HTMLLIElement[];
    rows.forEach((row, index) => {
      const active = index === this.highlightedIndex;
      row.classList.toggle("suggestion-cell-editor__option--active", active);
      row.setAttribute("aria-selected", active ? "true" : "false");
    });

    const activeRow = rows[this.highlightedIndex];
    if (activeRow) activeRow.scrollIntoView({ block: "nearest" });
    this.updateActiveDescendant();
  }

  private updateActiveDescendant(): void {
    if (this.highlightedIndex >= 0 && this.highlightedIndex < this.matches.length) {
      this.input.setAttribute("aria-activedescendant", this.rowId(this.highlightedIndex));
    } else {
      this.input.removeAttribute("aria-activedescendant");
    }
  }

  // ── Open/close state ────────────────────────────────────────────────────

  private openList(): void {
    this.listOpen = true;
    this.list.classList.add("suggestion-cell-editor__list--open");
    this.input.setAttribute("aria-expanded", "true");
  }

  private closeList(): void {
    this.listOpen = false;
    this.list.classList.remove("suggestion-cell-editor__list--open");
    this.input.setAttribute("aria-expanded", "false");
    this.input.removeAttribute("aria-activedescendant");
  }

  // ── Keyboard ─────────────────────────────────────────────────────────────

  private handleKeyDown(event: KeyboardEvent): void {
    const visibleCount = this.matches.length;

    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      this.engaged = true;
      event.preventDefault();
      // Reopening re-renders, which seeds the highlight now that the user has engaged.
      if (!this.listOpen) return this.applyFilter();
      if (visibleCount === 0) return;

      const step = event.key === "ArrowDown" ? 1 : -1;
      // From "nothing highlighted", Down takes the first row and Up takes the last.
      const from = this.highlightedIndex < 0 ? (step === 1 ? -1 : 0) : this.highlightedIndex;
      this.highlightedIndex = (from + step + visibleCount) % visibleCount;
      this.updateHighlight();
      return;
    }

    if (event.key === "Enter" || event.key === "Tab") {
      if (this.listOpen && this.highlightedIndex >= 0) {
        event.preventDefault();
        this.acceptRow(this.highlightedIndex);
      }
      return;
    }

    if (event.key === "Escape") {
      if (this.listOpen) {
        // First Escape closes the list only; a second reaches AG-Grid's cancel-edit.
        event.stopPropagation();
        this.closeList();
      }
    }
  }

  private acceptRow(index: number): void {
    const match = this.matches[index];
    if (!match) return;
    this.input.value = match.name;
    this.closeList();
    this.input.focus();
  }
}
